import { BadgeType } from "@prisma/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { handleApiCall } from "../api-error-handler";
import { privateAxios } from "../axiosInstances";
import badgeTypesQueryKeys, {
  badgesQueryKeys,
} from "../query-keys/badges.query-keys";
import { CreateBadgeTypeForm } from "../schemas/badges/create-badge-type.schema";
import {
  IssueTicketsForm,
  IssueTicketsWithoutDeliveryModeForm,
} from "../schemas/tickets/issue-tickets.schema";
import { toDatabaseFormat } from "@ac-expo/utils";

export const enableBadges = async (eventId: string) => {
  try {
    const { data } = await privateAxios.post<BadgeType>(
      `/events/${eventId}/badges/enable`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const disableBadges = async (eventId: string) => {
  try {
    const { data } = await privateAxios.post<BadgeType>(
      `/events/${eventId}/badges/disable`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useSetBadgesEnabledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, enable }: { eventId: string; enable: boolean }) =>
      enable ? enableBadges(eventId) : disableBadges(eventId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: badgesQueryKeys.event(eventId),
      });
    },
  });
};

export const enableBadge = async ({
  eventId,
  id,
  exhibitorId,
}: {
  eventId: string;
  id: string;
  exhibitorId?: string;
}) => {
  try {
    const url = exhibitorId
      ? `/events/${eventId}/badges/exhibitors/${exhibitorId}/${id}/enable`
      : `/events/${eventId}/badges/${id}/enable`;
    const { data } = await privateAxios.post<BadgeType>(url);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }

    throw error;
  }
};

export const disableBadge = async ({
  eventId,
  id,
  exhibitorId,
}: {
  eventId: string;
  id: string;
  exhibitorId?: string;
}) => {
  try {
    const url = exhibitorId
      ? `/events/${eventId}/badges/exhibitors/${exhibitorId}/${id}/disable`
      : `/events/${eventId}/badges/${id}/disable`;
    const { data } = await privateAxios.post<BadgeType>(url);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }

    throw error;
  }
};

export const useSetBadgeEnabledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      id,
      enable,
      exhibitorId,
    }: {
      eventId: string;
      id: string;
      enable: boolean;
      exhibitorId?: string;
    }) => {
      return enable
        ? enableBadge({ eventId, id, exhibitorId })
        : disableBadge({ eventId, id, exhibitorId });
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: badgesQueryKeys.event(eventId),
      });
    },
  });
};

export const enableUserBadge = async ({
  eventId,
  userId,
  structureId,
}: {
  eventId: string;
  userId: string | null;
  structureId: string | null;
}) => {
  try {
    const { data } = await privateAxios.post<BadgeType>(
      `/events/${eventId}/badges/user/enable`,
      {
        userId,
        structureId,
      }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const disableUserBadge = async ({
  eventId,
  userId,
  structureId,
}: {
  eventId: string;
  userId: string | null;
  structureId: string | null;
}) => {
  try {
    const { data } = await privateAxios.post<BadgeType>(
      `/events/${eventId}/badges/user/disable`,
      {
        userId,
        structureId,
      }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useSetUserBadgeEnabledMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      userId,
      structureId,
      enable,
    }: {
      eventId: string;
      userId: string | null;
      structureId: string | null;
      enable: boolean;
    }) =>
      enable
        ? enableUserBadge({ eventId, userId, structureId })
        : disableUserBadge({ eventId, userId, structureId }),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: badgesQueryKeys.event(eventId),
      });
    },
  });
};

type CreateBadgeTypeVariables = CreateBadgeTypeForm & { eventId: string };

const getDataFromFormData = (data: CreateBadgeTypeForm) => ({
  ...data,
  validityDates: data.validityDates.map(toDatabaseFormat),
  quantity: data.quantity === "Infini" ? null : data.quantity,
  isQuantityInfinite: data.quantity === "Infini",
  discountCodes: data.discountCodes.map((code) => ({
    ...code,
    validityDates: code.validityDates.map(toDatabaseFormat),
    quantity: code.quantity === "Infini" ? null : code.quantity,
  })),
});

export const createBadgeType = ({ eventId, ...body }: CreateBadgeTypeVariables) =>
  handleApiCall(() =>
    privateAxios.post<BadgeType>(`/events/${eventId}/badges/types`, getDataFromFormData(body))
  );

export const updateBadgeType = ({ eventId, ...body }: CreateBadgeTypeVariables) =>
  handleApiCall(() =>
    privateAxios.patch<BadgeType>(
      `/events/${eventId}/badges/types/${body.id}`,
      getDataFromFormData(body)
    )
  );

export const useBadgeTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateBadgeTypeVariables) =>
      values.id ? updateBadgeType(values) : createBadgeType(values),
    onSuccess: (_: unknown, { id, eventId }: CreateBadgeTypeVariables) => {
      queryClient.invalidateQueries({
        queryKey: badgeTypesQueryKeys.event(eventId),
      });
      if (id) {
        queryClient.invalidateQueries({
          queryKey: badgeTypesQueryKeys.get(eventId, id),
        });
      }
    },
  });
};

export const deleteBadgeType = async ({
  eventId,
  id,
}: {
  eventId: string;
  id: string;
}) => {
  try {
    const { data } = await privateAxios.delete<BadgeType>(
      `/events/${eventId}/badges/types/${id}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useDeleteBadgeTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBadgeType,
    onSuccess: (_, { eventId, id }) => {
      queryClient.invalidateQueries({
        queryKey: badgeTypesQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: badgeTypesQueryKeys.get(eventId, id),
      });
    },
  });
};

export const issueBadges = async ({
  eventId,
  itemId,
  tickets,
  deliveryMode,
}: {
  eventId: string;
} & IssueTicketsForm) => {
  try {
    // The mode is chosen once for the whole sending; the backend honours it per
    // exhibitor line and forces email on every other line.
    await privateAxios.post(
      `/events/${eventId}/badges/types/${itemId}/issue`,
      tickets.map((ticket) => ({ ...ticket, deliveryMode }))
    );
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useIssueBadgesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: issueBadges,
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: badgeTypesQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: badgesQueryKeys.event(eventId),
      });
    },
  });
};

export const assignBadge = async ({
  eventId,
  structureId,
  ...body
}: {
  eventId: string;
  structureId: string;
  isCommercial: boolean;
} & IssueTicketsWithoutDeliveryModeForm) => {
  try {
    await privateAxios.post(
      `/events/${eventId}/badges/assign-to-commercial/${body.itemId}/${structureId}`,
      body.tickets
    );
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useAssignBadgeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignBadge,
    onSuccess: (_, { eventId, structureId }) => {
      queryClient.invalidateQueries({
        queryKey: badgesQueryKeys.event(eventId, structureId),
      });
    },
  });
};

const transferBadge = async (
  eventId: string,
  badgeId: string,
  commercialId: string | null
) => {
  if (!eventId || !badgeId) return;
  try {
    const { data } = await privateAxios.post(
      `/events/${eventId}/badges/${badgeId}/transfer`,
      { commercialId }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useTransferBadgeMutation = (
  eventId: string,
  badgeId: string,
  exhibitorId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commercialId }: { commercialId: string | null }) =>
      transferBadge(eventId, badgeId, commercialId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: badgesQueryKeys.event(eventId, exhibitorId),
      });
    },
  });
};
